import { Button } from '@/components/ui/button';
import { Users } from 'lucide-react';
import { ScopeCard } from './ScopeCard';
import { EmptyState } from '@/components/ui/empty-state';
import type { ProjectScope } from '@/lib/schemas';
import type { ScopeStatus } from './ScopeStatusDropdown';

interface ProjectScopesListProps {
  scopes: ProjectScope[];
  onAddScope: () => void;
  onDeleteScope: (scopeId: string) => void;
  onUpdateStatus: (scopeId: string, newStatus: ScopeStatus) => void;
  className?: string;
  // Issue #13: Full edit mode props
  onUpdate?: (data: Partial<ProjectScope> & { id: string }) => void;
  organizations?: Array<{ id: string; name: string }>;
  scopeTypes?: Array<{ id: string; display_name: string; slug: string }>;
  contacts?: Array<{ id: string; name: string }>;
  onOrganizationChange?: (organizationId: string) => void;
  // Phase 6 Batch 2: Accordion props
  expandedScopeId?: string | null;
  onToggleScope?: (scopeId: string) => void;
  // Phase 7.3.3 Phase 2.5: Template support
  isTemplate?: boolean;
  // 9.5c: Hide header when wrapped in CollapsibleSection
  hideHeader?: boolean;
  // Phase 9.7bc: Set primary client scope
  onSetPrimary?: (scopeId: string) => void;
  // Phase 9.7bc: Multi-contact management
  onAddContact?: (scopeId: string, contactId: string) => void;
  onRemoveContact?: (scopeId: string, contactId: string) => void;
  onSetPrimaryContact?: (scopeId: string, contactId: string) => void;
  onSetInvoiceContact?: (scopeId: string, contactId: string) => void;
  projectId?: string;
  // Phase 10d.3: Emails of contacts who have portal access
  portalMemberEmails?: Set<string>;
}

/**
 * Displays project scopes (consultants, government agencies, etc.)
 *
 * NOTE: Client scopes are NOT shown here - they appear in the Project Overview section.
 * Pass only non-client scopes to this component (filter with `!scope_type_is_client`).
 *
 * Self scopes were removed in Phase 7 Batch 2.
 *
 * Features:
 * - Displays all scopes as ScopeCard components
 * - "Add Scope" button to trigger modal
 * - Scope count display
 * - Empty state when no scopes
 */
export function ProjectScopesList({
  scopes,
  onAddScope,
  onDeleteScope,
  onUpdateStatus,
  className,
  // Issue #13: Full edit mode props
  onUpdate,
  organizations,
  scopeTypes,
  contacts,
  onOrganizationChange,
  // Phase 6 Batch 2: Accordion props
  expandedScopeId,
  onToggleScope,
  // Phase 7.3.3 Phase 2.5: Template support
  isTemplate,
  // 9.5c: Hide header when wrapped in CollapsibleSection
  hideHeader = false,
  // Phase 9.7bc: Set primary client scope
  onSetPrimary,
  // Phase 9.7bc: Multi-contact management
  onAddContact,
  onRemoveContact,
  onSetPrimaryContact,
  onSetInvoiceContact,
  projectId,
  portalMemberEmails,
}: ProjectScopesListProps) {
  const scopeCount = scopes.length;
  const hasScopes = scopeCount > 0;
  // 9.7d: Count client scopes for conditional Primary badge display
  const clientScopeCount = scopes.filter(s => s.is_client_type).length;

  return (
    <div className={className}>
      {hasScopes ? (
        <>
          {/* Header with Add Button - 9.5c: Hide when wrapped in CollapsibleSection */}
          {!hideHeader ? (
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">Project Scopes</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {scopeCount} {scopeCount === 1 ? 'scope' : 'scopes'}
                </p>
              </div>
              <Button onClick={onAddScope}>Add Scope</Button>
            </div>
          ) : (
            <div className="flex justify-end mb-4">
              <Button onClick={onAddScope}>Add Scope</Button>
            </div>
          )}

          {/* Scopes List */}
          <div className="space-y-4">
            {scopes.map((scope) => (
              <ScopeCard
                key={scope.id}
                scope={scope}
                onDelete={onDeleteScope}
                onStatusChange={onUpdateStatus}
                // Issue #13: Pass full edit mode props when provided
                onUpdate={onUpdate}
                organizations={organizations}
                scopeTypes={scopeTypes}
                contacts={contacts}
                onOrganizationChange={onOrganizationChange}
                // Phase 6 Batch 2: Accordion props
                isExpanded={expandedScopeId === scope.id}
                onToggle={() => onToggleScope?.(scope.id)}
                // Phase 7.3.3 Phase 2.5: Template support
                isTemplate={isTemplate}
                // Phase 9.7bc: Set primary client scope
                onSetPrimary={onSetPrimary}
                // 9.7d: Only show Primary badge when 2+ client scopes
                clientScopeCount={clientScopeCount}
                // Phase 9.7bc: Multi-contact management
                onAddContact={onAddContact}
                onRemoveContact={onRemoveContact}
                onSetPrimaryContact={onSetPrimaryContact}
                onSetInvoiceContact={onSetInvoiceContact}
                projectId={projectId}
                portalMemberEmails={portalMemberEmails}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Header without Add Button - 9.5c: Hide when wrapped in CollapsibleSection */}
          {!hideHeader && (
            <div className="mb-4">
              <h2 className="text-xl font-semibold">Project Scopes</h2>
            </div>
          )}

          {/* Empty State with Add Button */}
          <EmptyState
            icon={Users}
            title="No scopes defined"
            description="Scopes track the organizations involved in your project"
            actionLabel="Add Scope"
            onAction={onAddScope}
          />
        </>
      )}
    </div>
  );
}
